import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  TOpsProgramDetailEx,
  TOpsMeetingKind,
} from '../../../requests/opsRegistrationsExtra';
import { downloadCsv, TProgramUtilisation } from '../../../requests/opsReports';
import { btnSecondary, Card, labelClass, tableClass } from '../ui';
import { downloadMeetingIcs as downloadIcs, money } from './shared';

/** How much of the program has been used, by whom, on what. */
const UtilisationCard: React.FC<{
  programId: string;
  program: TOpsProgramDetailEx;
  utilisation?: TProgramUtilisation;
}> = ({ programId, program, utilisation }) => {
  const [utilCsvBusy, setUtilCsvBusy] = useState(false);

  const utilisationCsv = async () => {
    setUtilCsvBusy(true);
    try {
      await downloadCsv(
        'ops/reports/program-utilisation',
        { programId },
        `program-utilisation-${program?.number ?? programId}.csv`,
      );
    } catch (err) {
      showToast(resolveApiMessage(err), 'error');
    } finally {
      setUtilCsvBusy(false);
    }
  };

  const downloadMeetingIcs = (k: TOpsMeetingKind) =>
    downloadIcs(programId, k, program.name);

  return (
    <Card
      title="Utilisation"
      className="mb-4"
      right={
        <span className="flex items-center gap-3">
          {program.meetings?.kickoff && (
            <button
              className="text-xs text-blue-primary"
              onClick={() => downloadMeetingIcs('kickoff')}
            >
              Kickoff .ics · {program.meetings.kickoff.start.slice(0, 10)}
            </button>
          )}
          {program.meetings?.wrap && (
            <button
              className="text-xs text-blue-primary"
              onClick={() => downloadMeetingIcs('wrap')}
            >
              Wrap .ics · {program.meetings.wrap.start.slice(0, 10)}
            </button>
          )}
          <button
            className={btnSecondary}
            onClick={utilisationCsv}
            disabled={utilCsvBusy || !utilisation}
          >
            {utilCsvBusy ? 'Preparing…' : 'Download CSV'}
          </button>
        </span>
      }
    >
      {!utilisation ? (
        <div className="px-5 py-6 text-sm text-gray-three">Counting…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-5 py-4 md:grid-cols-4">
            <div>
              <div className={labelClass}>Used of invested</div>
              <div className="text-xl font-bold text-gray-four [font-variant-numeric:tabular-nums]">
                {utilisation.balance.usedPct == null
                  ? '—'
                  : `${utilisation.balance.usedPct}%`}
              </div>
              <div className="text-xs text-gray-bold">
                {money(utilisation.balance.used)} of{' '}
                {money(utilisation.balance.invested)}
              </div>
            </div>
            <div>
              <div className={labelClass}>Members</div>
              <div className="text-xl font-bold text-gray-four [font-variant-numeric:tabular-nums]">
                {utilisation.members.enabled}
                <span className="text-sm font-normal text-gray-three">
                  {' '}
                  / {utilisation.members.total} enabled
                </span>
              </div>
            </div>
            <div>
              <div className={labelClass}>Classes</div>
              <div className="text-xl font-bold text-gray-four [font-variant-numeric:tabular-nums]">
                <Link
                  className="hover:text-blue-primary hover:underline"
                  to={`${
                    PATH.opsClasses
                  }?programId=${programId}&when=past&label=${encodeURIComponent(
                    program?.name ?? 'this program',
                  )}`}
                >
                  {utilisation.classes.held}
                </Link>
                <span className="text-sm font-normal text-gray-three">
                  {' '}
                  held ·{' '}
                  <Link
                    className="hover:text-blue-primary hover:underline"
                    to={`${
                      PATH.opsClasses
                    }?programId=${programId}&when=upcoming&label=${encodeURIComponent(
                      program?.name ?? 'this program',
                    )}`}
                  >
                    {utilisation.classes.upcoming} upcoming
                  </Link>
                </span>
              </div>
              <div className="text-xs text-gray-bold">
                {utilisation.classes.private} private ·{' '}
                {utilisation.classes.public} public
                {utilisation.classes.cancelled > 0 &&
                  ` · ${utilisation.classes.cancelled} cancelled`}
              </div>
            </div>
            <div>
              <div className={labelClass}>Registrations</div>
              <div className="text-xl font-bold text-gray-four [font-variant-numeric:tabular-nums]">
                {utilisation.registrations.total}
              </div>
              <div className="text-xs text-gray-bold">
                {utilisation.registrations.attended} attended ·{' '}
                {utilisation.registrations.noAttend} no-attend
                {utilisation.registrations.attendancePct != null &&
                  ` (${utilisation.registrations.attendancePct}% attended of those marked)`}
                {utilisation.registrations.upcoming > 0 &&
                  ` · ${utilisation.registrations.upcoming} upcoming`}
              </div>
            </div>
          </div>
          {(utilisation.byMonth.length > 0 ||
            utilisation.topCourses.length > 0) && (
            <div className="grid gap-4 border-t border-white-three px-5 py-4 lg:grid-cols-2">
              <div>
                <div className={labelClass}>Registrations by month</div>
                <table className={`${tableClass} mt-1`}>
                  <tbody>
                    {utilisation.byMonth.slice(-12).map((m) => (
                      <tr key={m.key}>
                        <td className="py-1 pr-3 text-sm text-gray-five">
                          {m.key}
                        </td>
                        <td className="py-1 pr-3 text-right text-sm [font-variant-numeric:tabular-nums]">
                          {m.registrations}
                        </td>
                        <td className="py-1 text-xs text-gray-three">
                          {m.attended} attended · {m.noAttend} no-attend
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {utilisation.byMonth.length > 12 && (
                  <div className="mt-1 text-xs text-gray-bold">
                    Last 12 months shown; the CSV has all{' '}
                    {utilisation.byMonth.length}.
                  </div>
                )}
              </div>
              <div>
                <div className={labelClass}>Top courses</div>
                <table className={`${tableClass} mt-1`}>
                  <tbody>
                    {utilisation.topCourses.map((c) => (
                      <tr key={c.key}>
                        <td className="py-1 pr-3 text-sm text-gray-five">
                          {c.key}
                        </td>
                        <td className="py-1 pr-3 text-right text-sm [font-variant-numeric:tabular-nums]">
                          {c.registrations}
                        </td>
                        <td className="py-1 text-xs text-gray-three">
                          {c.attended} attended · {c.noAttend} no-attend
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="border-t border-white-three px-5 py-3 text-xs text-gray-three">
            {utilisation.basis.registrations}. {utilisation.basis.classes}. The
            customer's "Export report" on their program page is this same
            report.
          </div>
        </>
      )}
    </Card>
  );
};

export default UtilisationCard;
